import { AfterViewInit, ChangeDetectionStrategy, Component, effect, input, signal } from '@angular/core';
import { ButtonComponent } from '../../../buttons/button/button.component';
import { CheckboxComponent } from '../../../interactive-labels/checkbox/checkbox.component';
import { TableFilter, TableFilterOption } from '../../models/table-filter';
import { InputSize } from '../../../enum/input-size.enum';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ButtonType } from '../../../enum/button-type.enum';
import { DateInputComponent } from '../../../form/inputs/date-input/date-input.component';

@Component({
  selector: 'lib-date-filter',
  imports: [ButtonComponent, CheckboxComponent, ReactiveFormsModule, DateInputComponent],
  templateUrl: './date-filter.component.html',
  styleUrl: './date-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateFilterComponent implements AfterViewInit {
  tableFilter = input.required<TableFilter>();

  protected readonly ButtonType = ButtonType;
  protected readonly InputSize = InputSize;

  useRange = signal<boolean>(false);
  minDateRange = signal<string | undefined>(undefined);
  maxDateRange = signal<string | undefined>(undefined);

  exactDateFormControlId = 'exactDate';
  minDateFormControlId = 'minDate';
  maxDateFormControlId = 'maxDate';
  exactDateFormControl = new FormControl<Date | null>(null);
  minDateFormControl = new FormControl<Date | null>(null);
  maxDateFormControl = new FormControl<Date | null>(null);

  tableFilterListener = effect(() => {
    const options = this.tableFilter().unsavedOptions ?? [];
    const exactDateOption = options.find(o => o.isExactValue);
    const minDateOption = options.find(o => o.isMinValue);
    const maxDateOption = options.find(o => o.isMaxValue);
    if (exactDateOption) {
      this.exactDateFormControl.setValue(exactDateOption.value as Date);
    }
    if (minDateOption) {
      this.minDateFormControl.setValue(minDateOption.value as Date);
    }
    if (maxDateOption) {
      this.maxDateFormControl.setValue(maxDateOption.value as Date);
    }

    if (minDateOption?.value || maxDateOption?.value) {
      this.useRange.set(true);
    }
  });

  exactValueListener = this.exactDateFormControl.valueChanges.subscribe(date => {
    const existingTableFilterOption = this.tableFilter().unsavedOptions?.find(o => o.isExactValue);
    if (existingTableFilterOption) {
      existingTableFilterOption.value = date;
      existingTableFilterOption.selected = date !== null;
    } else {
      const tableFilterOption = {
        name: this.exactDateFormControlId,
        selected: date !== null,
        value: date,
        isExactValue: true,
      } as TableFilterOption;
      this.tableFilter().unsavedOptions?.push(tableFilterOption);
    }
  });

  minValueListener = this.minDateFormControl.valueChanges.subscribe(date => {
    const existingTableFilterOption = this.tableFilter().unsavedOptions?.find(o => o.isMinValue);
    if (existingTableFilterOption) {
      existingTableFilterOption.value = date;
      existingTableFilterOption.selected = date !== null;
    } else {
      const tableFilterOption = {
        name: this.minDateFormControlId,
        selected: date !== null,
        value: date,
        isMinValue: true,
      } as TableFilterOption;
      this.tableFilter().unsavedOptions?.push(tableFilterOption);
    }
  });

  maxValueListener = this.maxDateFormControl.valueChanges.subscribe(date => {
    const existingTableFilterOption = this.tableFilter().unsavedOptions?.find(o => o.isMaxValue);
    if (existingTableFilterOption) {
      existingTableFilterOption.value = date;
      existingTableFilterOption.selected = date !== null;
    } else {
      const tableFilterOption = {
        name: this.maxDateFormControlId,
        selected: date !== null,
        value: date,
        isMaxValue: true,
      } as TableFilterOption;
      this.tableFilter().unsavedOptions?.push(tableFilterOption);
    }
  });

  constructor() {
    effect(() => {
      if (this.useRange()) {
        this.minDateFormControl.valueChanges.subscribe(date => {
          if (date) {
            this.maxDateRange.set(date.toISOString().split('T')[0]);
          }
        });

        this.maxDateFormControl.valueChanges.subscribe(date => {
          if (date) {
            this.minDateRange.set(date.toISOString().split('T')[0]);
          }
        });
      } else {
        this.minDateRange.set(undefined);
        this.maxDateRange.set(undefined);
      }
    });
  }

  ngAfterViewInit(): void {
    this.tableFilter()?.resetAll$.subscribe(() => {
      this.resetFormControls();
      this.useRange.set(false);
    });
  }

  rangeCheckboxClicked() {
    this.resetFormControls();
    this.useRange.set(!this.useRange());
  }

  resetFormControls() {
    this.exactDateFormControl.reset();
    this.minDateFormControl.reset();
    this.maxDateFormControl.reset();
  }

  resetClicked() {
    this.tableFilter().resetUnsavedOptions();
  }
}
